import styled, { css } from 'styled-components';
import { headerDesktopFullHeight, headerDesktopMinWidth } from 'src/containers';
import { LICBlock as LICBlockComponent } from '../LICBlock';
import { MovieCard as MovieCardComponent } from '../MovieCard';
import { TableOfContents as TableOfContentsComponent } from '../TableOfContents';

const anchorScrollHack = () => css`
  ${({ theme }) => css`
    /*
      We have to use this trick to prevent the block from hiding behind the
      fixed header because of the automatic scrolling to an anchor element
    */
    margin-top: -${theme.sz.s18};
    padding-top: ${theme.sz.s18};

    @media (min-width: ${headerDesktopMinWidth}) {
      margin-top: calc(-1 * (${headerDesktopFullHeight} + ${theme.sz.s3}));
      padding-top: calc(${headerDesktopFullHeight} + ${theme.sz.s3});
    }
  `}
`;

export const AnchorContainer = styled.div(
  () => css`
    ${anchorScrollHack};
  `,
);

export const CTAContentBlockContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.md};
    margin-top: ${theme.sz.s6};
    margin-bottom: ${theme.sz.s6};

    @media (min-width: ${theme.sz.md}) {
      margin-top: ${theme.sz.s8};
      margin-bottom: ${theme.sz.s8};
      margin-left: auto;
      margin-right: auto;
    }
  `,
);

export const Divider = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.md};
    height: ${theme.sz.s05};
    margin-top: ${theme.sz.s6};
    margin-bottom: ${theme.sz.s6};
    background: ${theme.co.secondary40};

    @media (min-width: ${theme.sz.md}) {
      margin-top: ${theme.sz.s8};
      margin-bottom: ${theme.sz.s8};
      margin-left: auto;
      margin-right: auto;
    }
  `,
);

export const Grid = styled.div(
  ({ theme }) => css`
    margin-left: auto;
    margin-right: auto;
    padding-top: ${theme.sz.s4};
    display: grid;
    grid-template-areas:
      'GridTop'
      'TableOfContents'
      'GridBottom';
    grid-template-columns: 1fr;
    gap: ${theme.sz.s6};

    @media (min-width: ${theme.sz.md}) {
      max-width: ${theme.sz.lg};
      padding-top: ${theme.sz.s8};
      gap: ${theme.sz.s8};
    }

    @media (min-width: ${theme.sz.lg}) {
      padding-bottom: ${theme.sz.s14};
    }

    @media (min-width: ${theme.sz.xxl}) {
      max-width: ${theme.sz.xxl};
      grid-template-areas:
        'GridTop TableOfContents'
        'GridBottom TableOfContents';
      grid-template-columns: ${theme.sz.lg} ${theme.sz.xs};
      justify-content: space-between;
    }
  `,
);

export const GridContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    padding-left: ${theme.sz.s6};
    padding-right: ${theme.sz.s6};
  `,
);

export const GridTop = styled.div(
  ({ theme }) => css`
    grid-area: GridTop;
    max-width: ${theme.sz.md};
    color: ${theme.co.secondary10};
    ${theme.typography.paragraph.base};

    @media (min-width: ${theme.sz.md}) {
      margin-left: auto;
      margin-right: auto;
      ${theme.typography.paragraph.l};
    }
  `,
);

export const GridBottom = styled.div(
  () => css`
    grid-area: GridBottom;
  `,
);

export const LICBlock = styled(LICBlockComponent)(
  ({ theme }) => css`
    @media (min-width: ${theme.sz.lg}) {
      margin-top: ${theme.sz.s6};
    }
  `,
);

export const Link = styled.a(
  ({ theme }) => css`
    color: ${theme.co.information30};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  `,
);

export const MovieCard = styled(MovieCardComponent)(
  ({ theme }) => css`
    ${anchorScrollHack};

    @media (min-width: ${theme.sz.md}) {
      margin-left: auto;
      margin-right: auto;
    }
  `,
);

export const MoviesFAQContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.md};
    margin-top: ${theme.sz.s6};

    @media (min-width: ${theme.sz.md}) {
      margin-top: ${theme.sz.s8};
      margin-left: auto;
      margin-right: auto;
    }
  `,
);

export const MemriseExperienceSecondBackground = styled.div(
  ({ theme }) => css`
    height: ${theme.sz.s40};
    background-color: ${theme.co.secondary99};
    box-shadow: 0 0 0 100vmax ${theme.co.secondary99};
    clip-path: inset(0 -100vmax);

    & + * {
      position: relative;
      top: -${theme.sz.s40};
      margin-bottom: -${theme.sz.s40};
    }
  `,
);

export const SideNoteBlockContainer = styled.div(
  ({ theme }) => css`
    @media (min-width: ${theme.sz.lg}) {
      margin-top: ${theme.sz.s6};
    }
  `,
);

export const TableOfContents = styled(TableOfContentsComponent)(
  ({ theme }) => css`
    grid-area: TableOfContents;

    @media (min-width: ${theme.sz.md}) {
      margin-left: auto;
      margin-right: auto;
    }

    @media (min-width: ${theme.sz.xxl}) {
      margin-left: unset;
      margin-right: unset;
    }
  `,
);

export const SideNoteBlockButtonContainer = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.sz.s6};
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.sz.md}) {
      flex-direction: row;
    }
  `,
);
